import type { TableProps as AntTableProps } from "antd";
import { Table as AntTable, Typography } from "antd";
import type { INTERNAL_SELECTION_ITEM } from "antd/es/table/hooks/useSelection";
import type { RowSelectionType, SelectionItem } from "antd/es/table/interface";
import cn from "classnames";
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";

type TableContextType = {
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: Dispatch<SetStateAction<React.Key[]>>;
};

export interface TableProps<T>
  extends AntTableProps<T>, Partial<TableContextType> {
  allowSelectAll?: boolean;
  fixed?: boolean;
  rowSelectors?: Array<
    Omit<SelectionItem, "onSelect"> & {
      onSelect: (keys: React.Key[]) => React.Key[];
    }
  >;
  entityName?: string;
  selections?: INTERNAL_SELECTION_ITEM[];
  selectableFilter?: (records: readonly T[] | undefined) => boolean;
  onBulkRemove?: (keys: React.Key[]) => void;
  onBulkUpdate?: (keys: React.Key[]) => void;
}

const FIXED_HEADER_TABLE_HEIGHT = "calc(100dvh - 16rem)";

type BuildSelectionsArgs = Pick<
  TableProps<object>,
  | "allowSelectAll"
  | "onBulkRemove"
  | "onBulkUpdate"
  | "rowSelectors"
  | "selections"
> & {
  anyItems: boolean;
  currentRowKeys: React.Key[];
  setCurrentRowKeys: Dispatch<SetStateAction<React.Key[]>>;
};

function buildSelections({
  allowSelectAll,
  anyItems,
  currentRowKeys,
  onBulkRemove,
  onBulkUpdate,
  rowSelectors,
  selections: customSelections,
  setCurrentRowKeys,
}: BuildSelectionsArgs) {
  const selections: INTERNAL_SELECTION_ITEM[] = [];

  if (anyItems && (allowSelectAll || onBulkRemove)) {
    selections.push(AntTable.SELECTION_ALL);
  }

  if (currentRowKeys.length) {
    if (customSelections) {
      selections.push(...customSelections);
    }

    if (onBulkUpdate) {
      selections.push({
        key: "bulkEdit",
        text: <FormattedMessage id="edit" defaultMessage="edit" />,
        onSelect: () => onBulkUpdate(currentRowKeys),
      });
    }

    if (onBulkRemove) {
      selections.push({
        key: "bulkRemove",
        text: (
          <Typography.Text type="danger">
            <FormattedMessage id="delete" defaultMessage="delete" />
          </Typography.Text>
        ),
        onSelect: () => onBulkRemove(currentRowKeys),
      });
    }
  }

  rowSelectors?.forEach(({ key, text, onSelect }) => {
    selections.push({
      key,
      text,
      onSelect: () => setCurrentRowKeys((keys) => [...new Set(onSelect(keys))]),
    });
  });

  return selections;
}

export function Table<T extends object>({
  selectedRowKeys,
  fixed,
  allowSelectAll,
  rowSelectors,
  entityName,
  onBulkRemove,
  onBulkUpdate,
  selectableFilter = () => true,
  setSelectedRowKeys,
  ...tableProps
}: TableProps<T>) {
  const [_selectedRowKeys, _setSelectedRowKeys] = useState<React.Key[]>([]);

  const currentRowKeys = selectedRowKeys || _selectedRowKeys;
  const setCurrentRowKeys = setSelectedRowKeys || _setSelectedRowKeys;

  const anyItems = !!tableProps?.dataSource?.length;

  const scroll = {
    x: "max-content",
    y: fixed && anyItems ? FIXED_HEADER_TABLE_HEIGHT : undefined,
  };

  const selections = buildSelections({
    allowSelectAll,
    anyItems,
    currentRowKeys,
    onBulkRemove,
    onBulkUpdate,
    rowSelectors,
    selections: tableProps.selections,
    setCurrentRowKeys,
  });

  const rowSelection =
    selectableFilter(tableProps.dataSource) && selections.length
      ? {
          type: "checkbox" as RowSelectionType,
          selectedRowKeys: currentRowKeys,
          selections,
          onChange: setCurrentRowKeys,
          getCheckboxProps: () => ({ tabIndex: -1 }),
        }
      : undefined;

  return (
    <AntTable
      rowSelection={rowSelection}
      scroll={scroll}
      {...tableProps}
      className={cn("notranslate", tableProps.className)}
    />
  );
}

Table.Summary = AntTable.Summary;

const TableContext = createContext<TableContextType>({
  selectedRowKeys: [],
  setSelectedRowKeys: () => {},
});

export function useTableContext() {
  return useContext(TableContext);
}

export function TableContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const value = useMemo(
    () => ({ selectedRowKeys, setSelectedRowKeys }),
    [selectedRowKeys]
  );

  return (
    <TableContext.Provider value={value}>{children}</TableContext.Provider>
  );
}
